import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useDingPlayer — HTMLAudio-based notification-ding player with explicit
 * output-device routing via `setSinkId`.
 *
 * Why an <audio> element instead of WebAudio?
 *   * Restaurants plug external speakers into the tablet AFTER the admin
 *     page is open. WebAudio on many Chromium builds does not follow OS
 *     default-output changes mid-session; HTMLAudio does.
 *   * `setSinkId()` (Chrome/Edge/Safari 17.4+) lets the owner explicitly
 *     pin the ding to a chosen output device (e.g. "USB Speaker") which
 *     survives OS default changes and page reloads.
 *
 * Persistence: localStorage stores a { deviceId, label } pair. On reload,
 * we first try to match by deviceId; if that has rotated (some browsers do
 * this per-session), we fall back to matching by label so the operator's
 * chosen physical speaker sticks.
 *
 * All device transitions go through applySink() which awaits setSinkId
 * before returning — callers can chain a test ding that is guaranteed to
 * play through the newly-selected device (not the previous one).
 */
const STORAGE_KEY = "rivaaz_admin_sink";

function readStoredSink() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { deviceId: "default", label: "" };
    // Back-compat with the old plain-string storage
    if (raw.startsWith("{")) return JSON.parse(raw);
    return { deviceId: raw, label: "" };
  } catch {
    return { deviceId: "default", label: "" };
  }
}

function writeStoredSink(deviceId, label) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ deviceId, label: label || "" }));
  } catch { /* quota / disabled */ }
}

export default function useDingPlayer(soundOn) {
  const audioRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(() => readStoredSink().deviceId);
  const [needsPermission, setNeedsPermission] = useState(false);
  const canPickDevice =
    typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;

  // Create the hidden audio element once on mount.
  useEffect(() => {
    const el = new Audio("/ding.wav");
    el.preload = "auto";
    el.volume = 1.0;
    audioRef.current = el;
    return () => {
      try {
        el.pause();
      } catch { /* ignore */ }
      audioRef.current = null;
    };
  }, []);

  // Enumerate audio-output devices AND reconcile the stored selection.
  // If the stored deviceId no longer exists (Chromium sometimes rotates
  // IDs per session), match by label; if that hits, adopt the new ID and
  // rewrite storage so subsequent reloads are stable.
  const refreshDevices = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
      setDevices([]);
      return [];
    }
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const outs = all.filter((d) => d.kind === "audiooutput");
      setDevices(outs);
      setNeedsPermission(outs.some((d) => !d.label));

      const stored = readStoredSink();
      const byId = outs.find((d) => d.deviceId === stored.deviceId);
      if (!byId && stored.label) {
        const byLabel = outs.find(
          (d) => d.label && d.label.trim() === stored.label.trim()
        );
        if (byLabel) {
          writeStoredSink(byLabel.deviceId, byLabel.label);
          setSelectedDeviceId(byLabel.deviceId);
        }
      }
      return outs;
    } catch {
      setDevices([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!canPickDevice) return;
    refreshDevices();
    const handler = () => refreshDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", handler);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", handler);
  }, [canPickDevice, refreshDevices]);

  // Apply the sinkId to the audio element on any selection change. This is
  // best-effort (fires after React commits) — callers that need
  // guaranteed sink-before-play should call selectDevice() and await it.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !canPickDevice) return;
    // Always call setSinkId — including for "default" — so switching back
    // to default cleanly detaches the pinned device.
    el.setSinkId(selectedDeviceId || "default").catch(() => {
      /* device disappeared or permission not granted */
    });
  }, [selectedDeviceId, canPickDevice]);

  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      await refreshDevices();
    } catch {
      /* user denied */
    }
  }, [refreshDevices]);

  // Async device selection — awaits setSinkId before resolving so callers
  // can safely test-play immediately after.
  const selectDevice = useCallback(
    async (deviceId) => {
      const el = audioRef.current;
      const found = devices.find((d) => d.deviceId === deviceId);
      const label = found?.label || "";
      writeStoredSink(deviceId, label);
      setSelectedDeviceId(deviceId);
      if (el && canPickDevice) {
        try {
          await el.setSinkId(deviceId || "default");
        } catch {
          /* silently fall back */
        }
      }
    },
    [devices, canPickDevice]
  );

  // Play the ding. Rewinds first so back-to-back new orders each ding.
  const forcePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    try {
      el.pause();
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked */ });
    } catch { /* ignore */ }
  }, []);

  const playDing = useCallback(() => {
    if (!soundOn) return;
    forcePlay();
  }, [soundOn, forcePlay]);

  // Test always plays regardless of soundOn state.
  const testDing = useCallback(() => forcePlay(), [forcePlay]);

  return {
    playDing,
    testDing,
    devices,
    selectedDeviceId,
    selectDevice,
    canPickDevice,
    needsPermission,
    requestPermission,
  };
}
